import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";
import FAQSection from "@/components/FAQSection";
import {
  PoundSterling,
  Play,
  Target,
  Video,
  Palette,
  Camera,
  FileText,
  Globe,
  GraduationCap,
  Sparkles,
  Clock,
  Check,
  Users,
  Layers,
  Handshake,
  BarChart3,
  Shield,
  ArrowRight
} from "lucide-react";

const fundingServices = [
  { icon: Target, title: "Internal Communications Strategy & Consultation", image: "https://customer-assets.emergentagent.com/job_23363ffb-eceb-44af-926f-252c6605d809/artifacts/twtcu27l_strategy.jpg" },
  { icon: Video, title: "Video Production", image: "https://customer-assets.emergentagent.com/job_23363ffb-eceb-44af-926f-252c6605d809/artifacts/kqg0da5i_IMG_1388.JPG" },
  { icon: Camera, title: "Photography", image: "https://customer-assets.emergentagent.com/job_23363ffb-eceb-44af-926f-252c6605d809/artifacts/k9tqn6on_keith%20camera.jpg" },
  { icon: Globe, title: "Website & Portal Development", image: "https://customer-assets.emergentagent.com/job_23363ffb-eceb-44af-926f-252c6605d809/artifacts/8qovvcyt_web%20development.jpg" },
  { icon: Palette, title: "Graphic Design", image: "https://customer-assets.emergentagent.com/job_23363ffb-eceb-44af-926f-252c6605d809/artifacts/ycyoh66w_graphic%20design.jpg" },
  { icon: FileText, title: "Content Creation", image: "https://customer-assets.emergentagent.com/job_23363ffb-eceb-44af-926f-252c6605d809/artifacts/ycf1kmnm_keith%201.jpg" },
  { icon: GraduationCap, title: "Training Content Production", image: "https://customer-assets.emergentagent.com/job_23363ffb-eceb-44af-926f-252c6605d809/artifacts/3qt58p5q_IMG_0126_1024x683.jpg" },
  { icon: Sparkles, title: "Animation & Motion Graphics", image: "https://customer-assets.emergentagent.com/job_23363ffb-eceb-44af-926f-252c6605d809/artifacts/6gg43wh7_animation.jpeg" },
];

const faqs = [
  {
    question: "What is the process to receive funding?",
    answer: "Firstly, register your interest. Then once our team have been in touch to ask a few more questions, you will be given an online application form. Once filled out (it includes a brief for your project), you will be given a quote for your project by the team. We will then process your application to see if you are eligible for funding (taking around 2 days). Once approved, we can then remove up to 50% of the cost of our quote and you will be sent over an agreement form to get started."
  },
  {
    question: "How do I know if I am eligible for funding?",
    answer: "You can apply for funding if your organisation has a UK address and you are either a VAT-registered limited company, a PLC who pays taxes in the UK or a registered charity (including universities). Sole traders or non UK based companies are unfortunately not eligible to apply."
  },
  {
    question: "Can we use the funding for projects with any agency?",
    answer: "No. The funding is specifically for projects carried out by ICA (The Internal Comms Agency). However, if you have received a quote from another agency, get in touch and we can see if we can match the quote and provide funding towards it too."
  },
  {
    question: "How long does the whole process take?",
    answer: "Once you have talked to the team and filled out an application form, you should know if your application has been successful in 2 working days. Once all gone through, we can start your project in 2 weeks."
  },
  {
    question: "Is there a limit I can apply for?",
    answer: "The funding covers either up to 50% of your project or a maximum of \u00a312,000 \u2014 whichever is less."
  },
  {
    question: "If I am not successful in my application, am I under any contract?",
    answer: "No. If your application is unsuccessful we can see what kind of discount we may be able to offer, but if you decide you cannot afford the project you are under no obligation to work with ICA at all."
  }
];

const FundingPage = () => {
  return (
    <div data-testid="funding-page" className="min-h-screen bg-[#050505]">
      <SEO
        title="Digital Development Funding for Internal Communications | Up to 50% Off"
        description="Apply for up to 50% or up to \u00a312,000 funding towards your next internal communications project. Available for UK organisations looking to improve employee engagement through video, design, training content and digital platforms."
        keywords="internal communications funding, digital development funding, employee engagement funding, training video funding, internal comms grant, UK business funding, communications project funding"
        canonical="/funding"
      />

      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-center" data-testid="funding-hero">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#050505] via-[#0a0a0a] to-[#050505]" />
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#00E5FF]/5 rounded-full blur-[200px]" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#00E5FF]/3 rounded-full blur-[150px]" />
        </div>

        <div className="container mx-auto px-4 md:px-8 relative z-10 pt-32">
          <div className="max-w-4xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-lg bg-[#00E5FF]/20 flex items-center justify-center">
                <PoundSterling className="w-6 h-6 text-[#00E5FF]" />
              </div>
              <span className="text-[#00E5FF] font-semibold uppercase tracking-wider">
                Digital Development Funding
              </span>
            </div>

            <h1 className="font-display text-5xl md:text-7xl tracking-tight leading-none text-white mb-6">
              GET UP TO 50%
              <br />
              <span className="text-[#00E5FF]">TOWARDS YOUR INTERNAL COMMS PROJECT</span>
            </h1>

            <p className="text-lg md:text-xl text-[#B3B3B3] max-w-3xl mb-4">
              ICA are now able to offer up to 50% or up to {"\u00A3"}12,000 funding for your next 
              internal communications project. If you are a UK based organisation looking to 
              transform how you communicate with your workforce but budgets have been tight, 
              this is the perfect opportunity.
            </p>

            <p className="text-white text-lg font-semibold mb-8">
              Limited funding available — provided on a first come, first served basis. <strong>Applications close April 4th.</strong>
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact">
                <Button
                  data-testid="funding-register-btn"
                  className="bg-[#00E5FF] text-black hover:bg-[#00B8CC] font-bold px-8 py-6 uppercase tracking-wider text-base"
                >
                  <Play className="w-5 h-5 mr-2 fill-black" />
                  Register Your Interest
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* What Funding Covers */}
      <section className="py-16 md:py-20 bg-[#0a0a0a]" data-testid="funding-services">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl tracking-wide text-white mb-4">
              WHAT THE FUNDING COVERS
            </h2>
            <p className="text-[#B3B3B3] text-lg">
              Apply the funding to any of our internal communications services.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
            {fundingServices.map((service, index) => (
              <div
                key={index}
                className="relative aspect-square rounded-lg overflow-hidden group border border-[#1F1F1F] hover:border-[#00E5FF]/30 transition-colors"
              >
                {service.image ? (
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                    decoding="async"
                  />
                ) : (
                  <div className="w-full h-full bg-[#0a0a0a] flex items-center justify-center">
                    <service.icon className="w-16 h-16 text-[#00E5FF]/20" />
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <service.icon className="w-4 h-4 text-[#00E5FF]" />
                  </div>
                  <span className="text-white text-sm font-medium leading-tight">{service.title}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 md:py-20" data-testid="funding-how-it-works">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl tracking-wide text-white mb-4">
              HOW IT WORKS
            </h2>
            <p className="text-[#B3B3B3] text-lg">
              Four simple steps to receive up to 50% funding for your internal communications project.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {[
              { step: "01", title: "Register Your Interest", desc: "Get in touch and tell us about your organisation and what you need." },
              { step: "02", title: "Submit Your Application", desc: "Complete a short application form including a brief for your project." },
              { step: "03", title: "Get Approved", desc: "We process your application in around 2 working days to confirm eligibility." },
              { step: "04", title: "Start Your Project", desc: "Once approved, up to 50% is removed from your quote and we get started." },
            ].map((item, index) => (
              <div
                key={index}
                data-testid={`funding-step-${index}`}
                className="p-6 bg-[#141414] rounded-lg border border-[#1F1F1F] hover:border-[#00E5FF]/30 transition-colors text-center"
              >
                <span className="text-[#00E5FF]/30 font-display text-4xl tracking-tight">{item.step}</span>
                <h3 className="font-display text-lg text-white mt-3 mb-2">{item.title}</h3>
                <p className="text-[#B3B3B3] text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link to="/contact">
              <Button
                data-testid="funding-howitworks-cta"
                className="bg-[#00E5FF] text-black hover:bg-[#00B8CC] font-bold px-8 py-6 uppercase tracking-wider text-base"
              >
                Register Your Interest
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Eligibility */}
      <section className="py-16 md:py-20 bg-[#0a0a0a]" data-testid="funding-eligibility">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Eligible */}
              <div className="p-8 bg-[#141414] rounded-lg border border-[#00E5FF]/20">
                <h3 className="font-display text-2xl text-[#00E5FF] mb-6">
                  WHO CAN APPLY
                </h3>
                <ul className="space-y-4">
                  {[
                    "UK based / registered organisations",
                    "VAT-registered limited companies",
                    "PLCs who pay taxes in the UK",
                    "Registered charities (including universities)",
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3 text-[#B3B3B3]">
                      <Check className="w-5 h-5 text-[#00E5FF] flex-shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Key Details */}
              <div className="p-8 bg-[#141414] rounded-lg border border-[#1F1F1F]">
                <h3 className="font-display text-2xl text-white mb-6">
                  KEY DETAILS
                </h3>
                <ul className="space-y-4">
                  {[
                    { icon: PoundSterling, text: "Up to 50% off or up to \u00a312,000 maximum \u2014 whichever is less" },
                    { icon: Clock, text: "Application processed in 2 working days" },
                    { icon: Play, text: "Project can start within 2 weeks of approval" },
                    { icon: Check, text: "No obligation if application is unsuccessful" },
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3 text-[#B3B3B3]">
                      <item.icon className="w-5 h-5 text-[#00E5FF] flex-shrink-0 mt-0.5" />
                      {item.text}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problems You Have / Problems We Solve */}
      <section className="py-16 md:py-20" data-testid="funding-problems">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl tracking-wide text-white mb-4">
              PROBLEMS YOU HAVE.{" "}
              <span className="text-[#00E5FF]">PROBLEMS WE SOLVE.</span>
            </h2>
            <p className="text-[#B3B3B3] text-lg">
              Use your funding to tackle the internal communications challenges holding your organisation back.
            </p>
          </div>

          <div className="space-y-4 max-w-5xl mx-auto">
            {[
              { problem: "Training materials exist but nobody can find them — or you don't have any at all", solve: "We create engaging training videos, visual guides and dedicated portals that your workforce actually uses." },
              { problem: "Company values are on the wall but not in the culture", solve: "We build ongoing values campaigns using video, design and storytelling that make values part of everyday working life." },
              { problem: "Important updates get buried in all-staff emails", solve: "We develop multi-channel strategies — video messages, visual summaries, digital displays — that cut through the noise." },
              { problem: "New starters feel lost for months with no proper onboarding", solve: "We create structured onboarding programmes with welcome videos, culture films and role-specific guides." },
              { problem: "Health & safety content ticks a box but doesn't change behaviour", solve: "We produce impactful safety films featuring your real sites and people that drive genuine behavioural change." },
              { problem: "Your careers page uses stock photos and doesn't reflect the real company", solve: "We capture your real people and culture — creating authentic employer brand content that attracts the right talent." },
            ].map((item, index) => (
              <div
                key={index}
                data-testid={`problem-${index}`}
                className="grid grid-cols-1 lg:grid-cols-2 bg-[#0a0a0a] border border-[#1F1F1F] rounded-xl overflow-hidden hover:border-[#00E5FF]/20 transition-colors"
              >
                <div className="p-6 border-b lg:border-b-0 lg:border-r border-[#1F1F1F] flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-red-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-red-400 text-xs font-bold">✕</span>
                  </div>
                  <p className="text-[#B3B3B3]">{item.problem}</p>
                </div>
                <div className="p-6 flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-[#00E5FF]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3 h-3 text-[#00E5FF]" />
                  </div>
                  <p className="text-white">{item.solve}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <FAQSection faqs={faqs} />

      {/* Why ICA - abbreviated */}
      <section className="py-16 md:py-20 bg-[#0a0a0a]" data-testid="funding-why-ica">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl tracking-wide text-white mb-4">
              WHY CHOOSE <span className="text-[#00E5FF]">ICA</span>
            </h2>
            <p className="text-[#B3B3B3] text-lg">
              Your funding goes further with an agency built specifically for internal communications.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              { icon: Users, title: "An Extension of Your Team", desc: "We operate as a fully embedded internal communications department, not an external supplier." },
              { icon: Layers, title: "All Expertise In-House", desc: "Strategy, video, design, photography, content and digital — all delivered by our own specialists." },
              { icon: Target, title: "Tailored, Not Off-the-Shelf", desc: "Bespoke communications strategies aligned to your culture, workforce and objectives." },
              { icon: Handshake, title: "Ongoing Partnership", desc: "Continuous collaboration with regular planning and review, not one-off projects." },
              { icon: BarChart3, title: "Outcomes-Focused", desc: "We measure success against employee engagement, message reach and behavioural change." },
              { icon: Shield, title: "Flexible Engagement", desc: "No long-term contracts. Scalable support that fits your organisation's needs." },
            ].map((item, index) => (
              <div
                key={index}
                className="p-6 bg-[#141414] rounded-lg border border-[#1F1F1F] hover:border-[#00E5FF]/20 transition-colors"
              >
                <div className="w-10 h-10 rounded-lg bg-[#00E5FF]/10 flex items-center justify-center mb-4">
                  <item.icon className="w-5 h-5 text-[#00E5FF]" />
                </div>
                <h3 className="font-display text-lg text-white mb-2">{item.title}</h3>
                <p className="text-[#B3B3B3] text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <Link to="/why-us">
              <Button
                data-testid="funding-why-us-link"
                variant="outline"
                className="bg-white/10 text-white hover:bg-white/20 border-white/10 px-6 py-4 uppercase tracking-wider text-sm"
              >
                Learn More About ICA
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-32 bg-[#050505] relative overflow-hidden" data-testid="funding-cta">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#00E5FF] rounded-full blur-[150px]" />
        </div>

        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-display text-4xl md:text-5xl tracking-wide text-white mb-6">
              DON'T MISS OUT
              <br />
              <span className="text-[#00E5FF]">APPLICATIONS END APRIL 4TH</span>
            </h2>
            <p className="text-[#B3B3B3] text-lg mb-8">
              Limited funding available on a first come, first served basis. Register your interest 
              today and find out if your organisation qualifies before the deadline.
            </p>
            <Link to="/contact">
              <Button
                data-testid="funding-final-cta"
                className="bg-[#00E5FF] text-black hover:bg-[#00B8CC] font-bold px-10 py-6 uppercase tracking-wider text-base"
              >
                Register Your Interest
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FundingPage;
